import { useEffect, useMemo, useRef, useState } from 'react';
import { SwitchCase } from '@toss/react';
import { logger } from '@zep/core-logger';
import { useCountryCode } from '@/shared/hooks';
import { useImageSearch } from '@/views/build/hook/imageSearch/useImageSearch';
import { SearchDefaultScreen } from './SearchDefaultScreen';
import { SearchedImagesScreen } from './SearchedImagesScreen';
import { SearchInput, type SearchInputRef } from './SearchInput';
import { SearchLoadingScreen } from './SearchLoadingScreen';
import { SearchNotFoundScreen } from './SearchNotFoundScreen';
import { useSearchedImageFile } from './useSearchedImageFile';
type ImageSearchScreenType = 'loading' | 'images' | 'notFound' | 'default';
export const SearchTabScreen = ({
  onUploadStart,
  imageUrl
}: {
  onUploadStart: (file: File) => void;
  imageUrl?: string;
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<SearchInputRef>(null);
  const [searchValue, setSearchValue] = useState<string>('');
  const [keyWordChanged, setKeyWordChanged] = useState<boolean>(false);
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [shouldFocusOnReturn, setShouldFocusOnReturn] = useState(false);
  const {
    countryCode
  } = useCountryCode();
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError
  } = useImageSearch(submittedSearch, countryCode);
  const images = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap(page => page?.photos ?? []);
  }, [data]);
  const handleSearch = () => {
    const trimmedSearchValue = searchValue.trim();
    if (!trimmedSearchValue) return;
    setSubmittedSearch(trimmedSearchValue);
    setKeyWordChanged(false);
    logger.track('image_search', {
      keyword: trimmedSearchValue
    });
  };
  const getSearchedImageFile = useSearchedImageFile();
  const handleImageSelect = async (imageUrl: string) => {
    const file = await getSearchedImageFile(imageUrl);
    if (file) {
      onUploadStart(file);
    }
    logger.track('searched_image_select', {
      keyword: submittedSearch
    });
  };
  const handleLinkClick = () => {
    setShouldFocusOnReturn(true);
  };
  const currentSearchStatus = useMemo<ImageSearchScreenType>(() => {
    const showLoadingState = isLoading;
    const hasImages = images.length > 0;
    const showNotFound = !isLoading && !isError && submittedSearch && !hasImages;
    if (showLoadingState) return 'loading';
    if (hasImages) return 'images';
    if (showNotFound) return 'notFound';
    return 'default';
  }, [isLoading, isError, submittedSearch, images.length]);
  useEffect(() => {
    // 검색 시 스크롤을 최상단으로 이동
    if (submittedSearch && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [submittedSearch]);
  useEffect(() => {
    // 링크 클릭 후 탭이 다시 활성화되면 검색 input에 포커스
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && shouldFocusOnReturn) {
        searchInputRef.current?.focus();
        setShouldFocusOnReturn(false);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [shouldFocusOnReturn]);
  return <div className="flex h-full flex-col" data-sentry-component="SearchTabScreen" data-sentry-source-file="SearchTabScreen.tsx">
      <SearchInput ref={searchInputRef} value={searchValue} onChange={value => {
      setSearchValue(value);
      setKeyWordChanged(true);
    }} onSearch={handleSearch} isSearchButtonVisible={!submittedSearch && !isLoading || keyWordChanged} data-sentry-element="SearchInput" data-sentry-source-file="SearchTabScreen.tsx" />
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto">
        <SwitchCase value={currentSearchStatus} defaultComponent={<SearchDefaultScreen onLinkClick={handleLinkClick} />} caseBy={{
        loading: <SearchLoadingScreen />,
        images: <SearchedImagesScreen images={images} isLoadingMore={isFetchingNextPage} hasNextPage={hasNextPage} onImageSelect={handleImageSelect} onLoadMore={fetchNextPage} />,
        notFound: <SearchNotFoundScreen onUploadStart={onUploadStart} imageUrl={imageUrl} />
      }} data-sentry-element="SwitchCase" data-sentry-source-file="SearchTabScreen.tsx" />
      </div>
    </div>;
};